import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { WorkOrderStatusBadge, WorkOrderPriorityBadge } from './WorkOrderStatusBadge';
import { WorkOrdersEmptyState } from '../shared/EmptyState';
import type { WorkOrder } from '../../types';

interface WorkOrdersTableProps {
  workOrders: WorkOrder[];
  onRowClick?: (workOrder: WorkOrder) => void;
  onStart?: (workOrder: WorkOrder) => void;
  onComplete?: (workOrder: WorkOrder) => void;
  /** Whether filters are currently applied */
  isFiltered?: boolean;
  /** Callback to clear filters */
  onClearFilters?: () => void;
  /** Callback to create new work order */
  onCreate?: () => void;
}

export function WorkOrdersTable({
  workOrders,
  onRowClick,
  onStart,
  onComplete,
  isFiltered,
  onClearFilters,
  onCreate,
}: WorkOrdersTableProps) {
  if (workOrders.length === 0) {
    return (
      <WorkOrdersEmptyState
        isFiltered={isFiltered}
        action={!isFiltered && onCreate ? {
          label: 'Create Work Order',
          onClick: onCreate,
        } : undefined}
        secondaryAction={isFiltered && onClearFilters ? {
          label: 'Clear Filters',
          onClick: onClearFilters,
        } : undefined}
      />
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>WO #</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead className="text-right">Items</TableHead>
            <TableHead className="w-[140px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workOrders.map((wo) => (
            <TableRow
              key={wo.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onRowClick?.(wo)}
            >
              <TableCell className="font-mono text-sm font-medium">
                {wo.work_order_number}
              </TableCell>
              <TableCell className="max-w-[200px] truncate">
                {wo.title}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {wo.account_name || '-'}
              </TableCell>
              <TableCell className="text-muted-foreground max-w-[120px] truncate">
                {wo.supplier_name || '-'}
              </TableCell>
              <TableCell>
                <WorkOrderStatusBadge status={wo.status} />
              </TableCell>
              <TableCell>
                <WorkOrderPriorityBadge priority={wo.priority} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(wo.due_date)}
              </TableCell>
              <TableCell className="text-right">
                <span className="text-sm">
                  {wo.total_picked || 0}/{wo.total_ordered || wo.item_count || 0}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  {wo.status === 'pending' && onStart && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onStart(wo)}
                    >
                      Start
                    </Button>
                  )}
                  {wo.status === 'in_progress' && onComplete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onComplete(wo)}
                    >
                      Complete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
